// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Weather service.
 *
 * Fetches coordinate-based forecasts from the BFF (`/api/v1/weather/forecasts`)
 * and folds the upstream timeseries into a per-day forecast the header widget
 * can render directly. The upstream contract keys each metric value onto a
 * point by the metric's `name`, so this module locates the temperature metric
 * (and optional cloud-cover / precipitation metrics) from the metric metadata
 * and aggregates the sub-daily points into daily lows, highs and a condition.
 */

import { get } from './api';
import { ENDPOINTS, WEATHER_LOCATION } from '../utils/constants';
import { MOCK_HEADER_FORECAST } from '../mocks/weather';
import type {
  DailyForecast,
  WeatherCondition,
  WeatherEnvelope,
  WeatherForecast,
  WeatherMetric,
  WeatherPoint,
} from '../types/weather';

function shouldMockWeather(): boolean {
  return process.env.REACT_APP_MOCK_WEATHER === 'true';
}

const MAX_DAYS = 6;

function isTemperature(m: WeatherMetric): boolean {
  const hay = `${m.quantityKind} ${m.name}`.toLowerCase();
  return hay.includes('temp');
}

function isDryBulb(m: WeatherMetric): boolean {
  return isTemperature(m) && m.name.toLowerCase().includes('dry');
}

function toUtcTimestamp(ts: string): string {
  return /Z|[+-]\d{2}:\d{2}$/.test(ts) ? ts : `${ts}Z`;
}

function isCloud(m: WeatherMetric): boolean {
  const hay = `${m.quantityKind} ${m.name}`.toLowerCase();
  return hay.includes('cloud');
}

function isPrecip(m: WeatherMetric): boolean {
  const hay = `${m.quantityKind} ${m.name}`.toLowerCase();
  return hay.includes('precip') || hay.includes('rain');
}

/** Degree suffix from a metric unit, e.g. "°F"/"°C"; falls back to "°". */
function unitSuffix(unit: string | undefined): string {
  if (!unit) return '°';
  const u = unit.toLowerCase();
  if (u.includes('f') || u.includes('fahrenheit')) return '°F';
  if (u.includes('c') || u.includes('celsius')) return '°C';
  return '°';
}

/** Read a point's numeric value for a metric name, or null if absent. */
function valueOf(point: WeatherPoint, metricName: string): number | null {
  const raw = point[metricName];
  if (typeof raw === 'number') return raw;
  if (typeof raw === 'string' && raw.trim() !== '' && !Number.isNaN(Number(raw))) {
    return Number(raw);
  }
  return null;
}

function dayKey(date: Date): string {
  return `${date.getUTCFullYear()}-${date.getUTCMonth()}-${date.getUTCDate()}`;
}

interface DayAccumulator {
  date: Date;
  temps: number[];
  clouds: number[];
  precip: number[];
}

function conditionFor(acc: DayAccumulator): WeatherCondition {
  const avg = (xs: number[]): number => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0);
  if (acc.precip.length && Math.max(...acc.precip) > 0.2) return 'rain';
  if (acc.clouds.length && avg(acc.clouds) >= 55) return 'cloud';
  return 'sun';
}

/** Fold a raw envelope into the render-ready forecast, or null if unusable. */
export function transformForecast(envelope: WeatherEnvelope | null): WeatherForecast | null {
  const ts = envelope?.data;
  const points = ts?.points ?? [];
  const metrics = ts?.metrics ?? [];
  if (points.length === 0 || metrics.length === 0) return null;

  const tempMetric = metrics.find(isTemperature);
  if (!tempMetric) return null;
  const cloudMetric = metrics.find(isCloud);
  const precipMetric = metrics.find(isPrecip);

  const buckets = new Map<string, DayAccumulator>();
  for (const point of points) {
    const temp = valueOf(point, tempMetric.name);
    if (temp === null) continue;
    const date = new Date(point.timestamp);
    if (Number.isNaN(date.getTime())) continue;
    const key = dayKey(date);
    let acc = buckets.get(key);
    if (!acc) {
      const midnight = new Date(
        Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
      );
      acc = { date: midnight, temps: [], clouds: [], precip: [] };
      buckets.set(key, acc);
    }
    acc.temps.push(temp);
    if (cloudMetric) {
      const c = valueOf(point, cloudMetric.name);
      if (c !== null) acc.clouds.push(c);
    }
    if (precipMetric) {
      const p = valueOf(point, precipMetric.name);
      if (p !== null) acc.precip.push(p);
    }
  }

  const days: DailyForecast[] = Array.from(buckets.values())
    .sort((a, b) => a.date.getTime() - b.date.getTime())
    .slice(0, MAX_DAYS)
    .map(acc => ({
      date: acc.date,
      condition: conditionFor(acc),
      min: Math.round(Math.min(...acc.temps)),
      max: Math.round(Math.max(...acc.temps)),
    }));

  if (days.length === 0) return null;

  const firstTemp = valueOf(points[0], tempMetric.name);

  return {
    current: firstTemp === null ? days[0].max : Math.round(firstTemp),
    unitSuffix: unitSuffix(tempMetric.unit),
    days,
  };
}

/**
 * Fetch and transform the forecast for the header location. Returns null when
 * the upstream response carries no usable data (e.g. an empty mock stub) so the
 * widget can degrade gracefully rather than throw.
 */
export async function getHeaderForecast(siteId: string): Promise<WeatherForecast | null> {
  if (!siteId) return null;
  if (shouldMockWeather()) {
    return MOCK_HEADER_FORECAST();
  }
  const params = new URLSearchParams({
    latitude: String(WEATHER_LOCATION.latitude),
    longitude: String(WEATHER_LOCATION.longitude),
    site_id: siteId,
  });
  const envelope = await get<WeatherEnvelope>(`${ENDPOINTS.WEATHER_FORECASTS}?${params}`);
  return transformForecast(envelope);
}

export interface ObservationsWindow {
  /** The site shown by the screen; used only for BFF authorization. */
  siteId: string;
  /** Window start, ISO-8601 date-time. */
  start: string;
  /** Window end, ISO-8601 date-time. */
  end: string;
}

/**
 * Measured weather at the header location over a window. Unlike `/forecasts`,
 * the observations route requires the window as well as the coordinates.
 *
 * Returns the raw envelope: callers on the Live view resample onto a plan's
 * time axis rather than bucketing into days, so the per-day fold in
 * `transformForecast` would throw away exactly what they need.
 */
export const getObservations = ({
  siteId,
  start,
  end,
}: ObservationsWindow): Promise<WeatherEnvelope> => {
  if (!siteId.trim()) {
    return Promise.reject(new Error('siteId is required'));
  }
  const params = new URLSearchParams({
    latitude: String(WEATHER_LOCATION.latitude),
    longitude: String(WEATHER_LOCATION.longitude),
    site_id: siteId,
    start,
    end,
  });
  return get<WeatherEnvelope>(`${ENDPOINTS.WEATHER_OBSERVATIONS}?${params}`);
};

/** A timestamped temperature reading, ready to resample onto a plan axis. */
export interface TemperatureSeries {
  points: { timestamp: string; value: number | null }[];
  /** The metric's own unit, e.g. `DEG_F`. Absent when no metric matched. */
  unit?: string;
}

/**
 * Pull the temperature curve out of a weather envelope, forecast or observed.
 *
 * Metric selection: prefers a dry-bulb metric (name contains "dry") so that
 * feeds with both dry- and wet-bulb series reliably show ambient air
 * temperature. Falls back to the first metric whose name/quantityKind contains
 * "temp" when no dry-bulb metric is present.
 *
 * Timestamp normalisation: offset-less ISO-8601 strings (e.g.
 * `"2026-08-27T04:00:00"`) are treated as UTC by appending `Z`. Without this
 * a non-UTC browser shifts every point by its local offset, placing readings
 * outside the plan axis and silently dropping them.
 */
export function toTemperatureSeries(envelope: WeatherEnvelope | null): TemperatureSeries {
  const ts = envelope?.data;
  const metrics = ts?.metrics ?? [];
  const metric = metrics.find(isDryBulb) ?? metrics.find(isTemperature);
  if (!metric) return { points: [] };

  const points = (ts?.points ?? [])
    .filter(point => typeof point?.timestamp === 'string')
    .map(point => ({
      timestamp: toUtcTimestamp(point.timestamp),
      value: valueOf(point, metric.name),
    }));

  return { points, unit: metric.unit };
}
